import {
  WithTheme,
  useAtlasCoreThemeTokens,
  useAtlasSemanticThemeTokens,
} from '@issuemanager/apps/client/theme';
import { Box, Typography, styled, type BoxProps } from '@mui/material';
import { type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from '../../icons';

type StyledDropdownLikeProps = WithTheme<BoxProps> & { readOnly?: boolean };

const StyledDropdownLike = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'readOnly',
})<StyledDropdownLikeProps>(({ theme, readOnly }) => ({
  display: 'flex',
  height: 40,
  alignItems: 'center',
  borderRadius: 4,
  borderColor: theme.tokens.core.color.legacy.medium.value,
  borderWidth: 1,
  borderStyle: 'solid',
  background: readOnly
    ? theme.tokens.semantic.color.background.disabled.value
    : theme.tokens.semantic.color.background.container.value,
  width: '100%',
  cursor: 'pointer',
}));

const StyledBox = styled(Box)`
  width: fit-content;
  display: flex;
  justify-content: flex-end;
  padding-right: ${({ theme }) => theme.spacing(2)};
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  flex: 1 0 0;
  & svg {
    display: flex;
    height: 24px;
    width: 24px;
    justify-content: center;
    align-items: center;
  }
`;

export type FakeDropdownBoxProps = BoxProps & {
  readOnly?: boolean;
  placeholder?: string;
};

export function FakeDropdownBox({
  readOnly,
  placeholder,
  children,
  onClick,
  onKeyDown,
  ...rest
}: FakeDropdownBoxProps): JSX.Element {
  const { t } = useTranslation('common');
  const core = useAtlasCoreThemeTokens();
  const semantic = useAtlasSemanticThemeTokens();

  const isEmpty = !children;
  const displayText = isEmpty
    ? placeholder ?? t('filters.dropdownDefault')
    : children;

  return (
    <StyledDropdownLike
      readOnly={readOnly}
      {...(!readOnly && { onClick, onKeyDown, 'aria-readonly': true })}
      {...rest}
    >
      <Typography
        px={2}
        py={0}
        noWrap
        color={
          isEmpty && placeholder ? semantic.color.text.muted.value : undefined
        }
      >
        {displayText}
      </Typography>
      <StyledBox>
        <ChevronDown active activeColor={core.color.legacy.blackNear.value} />
      </StyledBox>
    </StyledDropdownLike>
  );
}
